/**
 * WallSegment - מחלקה המייצגת קטע קיר או מכשול במערכת FOV מבוססת קטעים
 * 
 * מחלקה זו משמשת לייצוג של קטעי קווים ישרים שחוסמים ראות/אור במשחק
 * כל קטע מוגדר על ידי נקודת התחלה, נקודת סיום, עובי ומאפיינים נוספים
 */

class WallSegment {
  /**
   * יוצר קטע קיר חדש
   * @param {number} x1 - קואורדינטת X של נקודת ההתחלה
   * @param {number} y1 - קואורדינטת Y של נקודת ההתחלה
   * @param {number} x2 - קואורדינטת X של נקודת הסיום
   * @param {number} y2 - קואורדינטת Y של נקודת הסיום
   * @param {Object} options - אפשרויות נוספות לקטע
   * @param {number} options.thickness - עובי הקטע (ברירת מחדל: 2)
   * @param {string} options.color - צבע הקטע (ברירת מחדל: #000000)
   * @param {string} options.type - סוג הקטע: 'wall' (קיר רגיל), 'polygon' (חלק מפוליגון), 'rectangle' (חלק ממלבן), 'circle' (חלק מעיגול)
   * @param {string} options.parentId - מזהה של הצורה המקורית שהקטע שייך אליה
   * @param {string} options.id - מזהה ייחודי לקטע (אם לא מוגדר, ייווצר אוטומטית)
   */
  constructor(x1, y1, x2, y2, options = {}) {
    this.x1 = x1;
    this.y1 = y1;
    this.x2 = x2;
    this.y2 = y2;
    
    // הגדרת מאפיינים נוספים עם ערכי ברירת מחדל
    this.thickness = options.thickness || 2;
    this.color = options.color || '#000000';
    this.type = options.type || 'wall';
    this.parentId = options.parentId || null;
    
    // יצירת מזהה ייחודי אם לא סופק
    this.id = options.id || this.generateId();
    
    // חישוב אורך הקטע (שימושי לחישובים שונים)
    this.length = this.calculateLength();
  }
  
  /**
   * יוצר מזהה ייחודי עבור הקטע
   * @returns {string} מזהה ייחודי
   */
  generateId() {
    return `wall-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
  }
  
  /**
   * מחשב את אורך הקטע
   * @returns {number} אורך הקטע
   */
  calculateLength() {
    const dx = this.x2 - this.x1;
    const dy = this.y2 - this.y1;
    return Math.sqrt(dx * dx + dy * dy);
  }
  
  /**
   * מחשב את המרחק המינימלי מנקודה לקטע
   * @param {number} x - קואורדינטת X של הנקודה
   * @param {number} y - קואורדינטת Y של הנקודה
   * @returns {number} המרחק המינימלי
   */
  distanceToPoint(x, y) {
    const x1 = this.x1;
    const y1 = this.y1;
    const x2 = this.x2;
    const y2 = this.y2;
    
    // חישוב המרחק המינימלי מנקודה לקטע
    // אם הנקודה נמצאת בטווח הקטע, המרחק הוא המרחק האנכי
    // אחרת, המרחק הוא המרחק לנקודת הקצה הקרובה ביותר
    
    const A = x - x1;
    const B = y - y1;
    const C = x2 - x1;
    const D = y2 - y1;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    
    if (lenSq !== 0) {
      param = dot / lenSq;
    }
    
    let xx, yy;
    
    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }
    
    const dx = x - xx;
    const dy = y - yy;
    
    return Math.sqrt(dx * dx + dy * dy);
  }
  
  /**
   * בודק אם קטע אחר חופף לקטע הנוכחי
   * @param {WallSegment} otherSegment - הקטע האחר לבדיקה
   * @returns {boolean} האם הקטעים חופפים
   */
  overlaps(otherSegment) {
    // פונקציית עזר לחישוב מכפלת וקטורים
    const crossProduct = (ax, ay, bx, by) => ax * by - ay * bx;
    
    // בדיקה אם שני קטעים נחתכים
    const p1 = [this.x1, this.y1];
    const p2 = [this.x2, this.y2];
    const p3 = [otherSegment.x1, otherSegment.y1];
    const p4 = [otherSegment.x2, otherSegment.y2];
    
    const d1 = crossProduct(p4[0] - p3[0], p4[1] - p3[1], p1[0] - p3[0], p1[1] - p3[1]);
    const d2 = crossProduct(p4[0] - p3[0], p4[1] - p3[1], p2[0] - p3[0], p2[1] - p3[1]);
    const d3 = crossProduct(p2[0] - p1[0], p2[1] - p1[1], p3[0] - p1[0], p3[1] - p1[1]);
    const d4 = crossProduct(p2[0] - p1[0], p2[1] - p1[1], p4[0] - p1[0], p4[1] - p1[1]);
    
    // אם הסימנים שונים, הקטעים נחתכים
    return (d1 * d2 <= 0) && (d3 * d4 <= 0);
  }
  
  /**
   * מחשב את נקודת החיתוך עם קטע אחר (אם קיימת)
   * @param {WallSegment} otherSegment - הקטע האחר
   * @returns {Object|null} נקודת החיתוך {x, y} או null אם אין חיתוך
   */
  findIntersection(otherSegment) {
    const x1 = this.x1;
    const y1 = this.y1;
    const x2 = this.x2;
    const y2 = this.y2;
    const x3 = otherSegment.x1;
    const y3 = otherSegment.y1;
    const x4 = otherSegment.x2;
    const y4 = otherSegment.y2;
    
    // בדיקת חיתוך בין שני קטעים
    
    const denominator = ((y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1));
    
    // מקרה של קטעים מקבילים
    if (Math.abs(denominator) < 0.0001) return null;
    
    const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
    const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;
    
    // נקודת החיתוך חייבת להיות על שני הקטעים
    if (ua < 0 || ua > 1 || ub < 0 || ub > 1) return null;
    
    // חישוב נקודת החיתוך
    const x = x1 + ua * (x2 - x1);
    const y = y1 + ua * (y2 - y1);
    
    return { x, y };
  }
  
  /**
   * יוצר עותק של הקטע הנוכחי
   * @returns {WallSegment} עותק חדש של הקטע
   */
  clone() {
    return new WallSegment(this.x1, this.y1, this.x2, this.y2, {
      thickness: this.thickness,
      color: this.color,
      type: this.type,
      parentId: this.parentId,
      id: this.generateId() // מייצר מזהה חדש לעותק
    });
  }
  
  /**
   * מפצל את הקטע לשני קטעים בנקודה מסוימת
   * @param {number} x - קואורדינטת X של נקודת הפיצול
   * @param {number} y - קואורדינטת Y של נקודת הפיצול
   * @returns {Array<WallSegment>} מערך של שני הקטעים החדשים
   */
  splitAt(x, y) {
    // יצירת שני קטעים חדשים מהקטע המקורי
    const segment1 = new WallSegment(this.x1, this.y1, x, y, {
      thickness: this.thickness,
      color: this.color,
      type: this.type,
      parentId: this.parentId
    });
    
    const segment2 = new WallSegment(x, y, this.x2, this.y2, {
      thickness: this.thickness,
      color: this.color,
      type: this.type,
      parentId: this.parentId
    });
    
    return [segment1, segment2];
  }
  
  /**
   * המרה למבנה נתונים פשוט עבור שמירה ב-JSON
   * @returns {Object} מבנה נתונים המייצג את הקטע
   */
  toJSON() {
    return {
      id: this.id,
      x1: this.x1,
      y1: this.y1,
      x2: this.x2,
      y2: this.y2,
      thickness: this.thickness,
      color: this.color,
      type: this.type,
      parentId: this.parentId
    };
  }
  
  /**
   * יוצר מופע WallSegment מתוך אובייקט JSON
   * @param {Object} data - נתוני JSON
   * @returns {WallSegment} מופע חדש של קטע קיר
   */
  static fromJSON(data) {
    return new WallSegment(data.x1, data.y1, data.x2, data.y2, {
      thickness: data.thickness,
      color: data.color,
      type: data.type,
      parentId: data.parentId,
      id: data.id
    });
  }
}

export default WallSegment; 